import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Card,
  CardBody,
  Flex,
  Icon,
  Image,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FinalizedPaymentProofItemDto } from '@diamond/shared/types';
import { Button as SharedButton } from '@diamond/shared/ui';
import { usePaymentProofOcrForm } from '@diamond/sol-buyer/utils';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { DropzoneOcr } from './DropzoneOcr';
import { OcrPreviewCard } from './OcrPreviewCard';

interface PaymentProofUploaderOcrProps {
  accessToken: string;
  orderPaymentId: string;
  isAccordionOpen?: boolean;
  openUploadRequestKey?: number;
  onAccordionChange?: (isOpen: boolean) => void;
  onPreviewProof?: (url: string) => void;
  onSuccess?: () => void;
  onUploadReady?: (open: (() => void) | null) => void;
}

const getSubmittedProofFileName = (proof: FinalizedPaymentProofItemDto) =>
  proof.original_file_name ?? 'Bukti pembayaran';

const formatCurrency = (amount: number | null | undefined) => {
  if (typeof amount !== 'number') return '-';

  return `Rp${amount.toLocaleString('id-ID')}`;
};

interface SubmittedProofsPreviewProps {
  proofs: FinalizedPaymentProofItemDto[];
  showOcrDetails: boolean;
  canCancelAdd: boolean;
  onAdd: () => void;
  onCancelAdd: () => void;
  onPreviewProof?: (url: string) => void;
}

const SubmittedProofsPreview = ({
  proofs,
  showOcrDetails,
  canCancelAdd,
  onAdd,
  onCancelAdd,
  onPreviewProof,
}: SubmittedProofsPreviewProps) => (
  <Stack gap={4}>
    <Flex align="center" justify="space-between">
      <Text color="#999999" fontSize="16px" fontWeight={400} lineHeight="24px">
        Preview
      </Text>
      {canCancelAdd ? (
        <Button
          variant="ghost"
          size="sm"
          bgColor="gray.customGray"
          color="pink"
          fontSize="12px"
          fontWeight={500}
          h="32px"
          minW="auto"
          px={2}
          iconSpacing={2}
          leftIcon={<Icon as={Close} boxSize="16px" />}
          sx={{
            '& svg': {
              color: 'inherit',
              fill: 'currentColor',
            },
          }}
          textColor="pink"
          _active={{
            bg: 'gray.33',
            color: 'pink',
            textColor: 'pink',
          }}
          _hover={{
            bg: 'gray.33',
            color: 'pink',
            textColor: 'pink',
          }}
          onClick={onCancelAdd}
        >
          Batalkan
        </Button>
      ) : !showOcrDetails ? (
        <Button
          variant="ghost"
          size="sm"
          color="blue"
          fontSize="12px"
          fontWeight={500}
          h="32px"
          minW="auto"
          px={2}
          iconSpacing={2}
          leftIcon={<Icon as={Add} boxSize="16px" />}
          isDisabled={proofs.length >= 5}
          onClick={onAdd}
        >
          Tambah
        </Button>
      ) : null}
    </Flex>

    <Flex gap="24px" wrap="wrap">
      {proofs.map((proof) => (
        <Stack
          key={proof.id}
          bg={showOcrDetails ? '#F1F8FF' : undefined}
          borderRadius={showOcrDetails ? '8px' : undefined}
          direction={showOcrDetails ? 'row' : 'column'}
          gap={showOcrDetails ? '12px' : 2}
          p={showOcrDetails ? '16px' : undefined}
          w={showOcrDetails ? '330px' : '120px'}
        >
          <Button
            aria-label={`Lihat bukti pembayaran ${getSubmittedProofFileName(
              proof
            )}`}
            h="120px"
            minW="120px"
            p={0}
            type="button"
            variant="unstyled"
            w="120px"
            onClick={() => onPreviewProof?.(proof.url)}
          >
            <Image
              src={proof.url}
              alt={getSubmittedProofFileName(proof)}
              w="120px"
              h="120px"
              borderRadius="4px"
              borderWidth="1px"
              borderColor="#E5E5EA"
              objectFit="cover"
              fallback={
                <Flex
                  align="center"
                  bg="gray.100"
                  borderRadius="4px"
                  h="120px"
                  justify="center"
                  w="120px"
                >
                  <Text color="#999999" fontSize="12px">
                    Preview
                  </Text>
                </Flex>
              }
            />
          </Button>
          <Stack flex={showOcrDetails ? 1 : undefined} minW={0} spacing={3}>
            <Text
              color="#373f41"
              fontSize="14px"
              fontWeight={400}
              lineHeight="20px"
              noOfLines={1}
            >
              {getSubmittedProofFileName(proof)}
            </Text>

            {showOcrDetails && (
              <Stack spacing={2}>
                <Stack spacing={1}>
                  <Text color="#373f41" fontSize="12px" lineHeight="16px">
                    Nominal
                  </Text>
                  <Text
                    color="#373f41"
                    fontSize="16px"
                    fontWeight={600}
                    lineHeight="20px"
                  >
                    {formatCurrency(proof.ocr?.amount)}
                  </Text>
                </Stack>
                <Stack spacing={1}>
                  <Text color="#373f41" fontSize="12px" lineHeight="16px">
                    Bank Pengirim
                  </Text>
                  <Text
                    color="#373f41"
                    fontSize="16px"
                    fontWeight={600}
                    lineHeight="20px"
                  >
                    {proof.ocr?.sender_bank ?? '-'}
                  </Text>
                </Stack>
                <Stack spacing={1}>
                  <Text color="#373f41" fontSize="12px" lineHeight="16px">
                    Nama Pemilik Rekening
                  </Text>
                  <Text
                    color="#373f41"
                    fontSize="16px"
                    fontWeight={600}
                    lineHeight="20px"
                  >
                    {proof.ocr?.sender_account_name ?? '-'}
                  </Text>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      ))}
    </Flex>
  </Stack>
);

export const PaymentProofUploaderOcr = ({
  accessToken,
  orderPaymentId,
  isAccordionOpen,
  openUploadRequestKey = 0,
  onAccordionChange,
  onPreviewProof,
  onSuccess,
  onUploadReady,
}: PaymentProofUploaderOcrProps) => {
  const [internalAccordionOpen, setInternalAccordionOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isAddingProof, setIsAddingProof] = useState(false);
  const dropzoneOpenRef = useRef<(() => void) | null>(null);
  const lastOpenUploadRequestKeyRef = useRef(openUploadRequestKey);
  const pendingOpenUploadRef = useRef(false);
  const {
    cards,
    submittedProofs,
    isSubmitting,
    isPollingTimedOut,
    canSubmit,
    form,
    uploadFiles,
    deleteFile,
    submitProof,
    resetPollingTimer,
  } = usePaymentProofOcrForm({
    accessToken,
    orderPaymentId,
    onSuccess: () => {
      setIsAddingProof(false);
      onSuccess?.();
    },
  });

  const resolvedAccordionOpen =
    typeof isAccordionOpen === 'boolean'
      ? isAccordionOpen
      : internalAccordionOpen;

  const manualOverrides = form.watch('manual_overrides');
  const remainingFileSlots = Math.max(
    5 - submittedProofs.length - cards.length,
    0
  );

  const showInfoBanner = cards.length === 0 || isPollingTimedOut;
  const hasSubmittedProofs = submittedProofs.length > 0;
  const showStagedForm =
    !hasSubmittedProofs || isAddingProof || cards.length > 0;
  const stagedCardsMarginTop =
    showInfoBanner || hasSubmittedProofs ? 4 : 0;
  const canOpenDropzone = remainingFileSlots > 0 && !isSubmitting;

  const handleDropzoneReady = useCallback(
    (open: (() => void) | null) => {
      const safeOpen = open
        ? () => {
            if (!canOpenDropzone) return;
            open();
          }
        : null;

      dropzoneOpenRef.current = safeOpen;
      onUploadReady?.(safeOpen);

      if (safeOpen && pendingOpenUploadRef.current) {
        pendingOpenUploadRef.current = false;
        safeOpen();
      }
    },
    [canOpenDropzone, onUploadReady]
  );

  const handleAccordionChange = (expandedIndex: number | number[]) => {
    const isOpen = Array.isArray(expandedIndex)
      ? expandedIndex.includes(0)
      : expandedIndex === 0;

    if (onAccordionChange) {
      onAccordionChange(isOpen);
    } else {
      setInternalAccordionOpen(isOpen);
    }
  };

  useEffect(() => {
    if (openUploadRequestKey === lastOpenUploadRequestKeyRef.current) {
      return;
    }

    lastOpenUploadRequestKeyRef.current = openUploadRequestKey;

    if (!canOpenDropzone) {
      return;
    }

    if (!showStagedForm) {
      pendingOpenUploadRef.current = true;
      setIsAddingProof(true);
      return;
    }

    dropzoneOpenRef.current?.();
  }, [canOpenDropzone, openUploadRequestKey, showStagedForm]);

  return (
    <FormProvider {...form}>
      <Card
        id="submit-payment-proof"
        shadow="none"
        borderRadius="8px"
        borderWidth="1px"
        borderColor="gray.33"
        maxW="378px"
        style={{ scrollMarginBlockStart: '10rem' }}
        w="100%"
      >
        <CardBody p={0}>
          <Accordion
            allowToggle
            index={resolvedAccordionOpen ? [0] : []}
            onChange={handleAccordionChange}
          >
            <AccordionItem border="none">
              <AccordionButton
                borderBottom="1px dashed"
                borderColor="gray.33"
                px="24px"
                pt="24px"
                pb="20px"
              >
                <Flex align="center" gap={3} flex={1}>
                  <Icon as={ReceiptLongOutlined} boxSize="26px" color="blue" />
                  <Text
                    fontSize="20px"
                    fontWeight={600}
                    color="#373f41"
                    lineHeight="24px"
                  >
                    Bukti Pembayaran
                  </Text>
                </Flex>
                <AccordionIcon />
              </AccordionButton>

              <AccordionPanel px="24px" pb="24px" pt="20px">
                {hasSubmittedProofs && (
                  <SubmittedProofsPreview
                    proofs={submittedProofs}
                    showOcrDetails={showStagedForm}
                    canCancelAdd={isAddingProof && cards.length === 0}
                    onAdd={() => setIsAddingProof(true)}
                    onCancelAdd={() => setIsAddingProof(false)}
                    onPreviewProof={onPreviewProof}
                  />
                )}

                {showStagedForm && (
                  <>
                    {showInfoBanner && (
                      <Flex
                        mt={hasSubmittedProofs ? 4 : 0}
                        bg="#FFF9E1"
                        borderRadius="4px"
                        px={4}
                        py={2}
                        gap={3}
                        align="center"
                      >
                        <Icon as={InfoOutlined} color="orange" boxSize="20px" />
                        <Text fontSize="12px" color="#131415" flex={1}>
                          {isPollingTimedOut
                            ? 'Bukti transfer masih dianalisa, coba lagi nanti.'
                            : 'Segera unggah bukti pembayaran agar PO diproses'}
                        </Text>
                        {isPollingTimedOut && (
                          <Button
                            size="sm"
                            variant="link"
                            color="blue"
                            onClick={resetPollingTimer}
                          >
                            Refresh
                          </Button>
                        )}
                      </Flex>
                    )}

                    {cards.length > 0 && (
                      <Stack gap={4} mt={stagedCardsMarginTop}>
                        {cards.map((card, index) => {
                          const fallbackFieldIndex = cards
                            .slice(0, index)
                            .filter(
                              (c) =>
                                c.state === 'manual_fallback' ||
                                c.state === 'duplicate' ||
                                c.state === 'success'
                            ).length;
                          const manualOverrideIndex = manualOverrides.findIndex(
                            (override) =>
                              override?.ocr_job_id === card.item.id
                          );
                          const fieldIndex =
                            manualOverrideIndex >= 0
                              ? manualOverrideIndex
                              : fallbackFieldIndex;

                          return (
                            <OcrPreviewCard
                              key={card.item.id}
                              item={card.item}
                              state={card.state}
                              fieldIndex={fieldIndex}
                              isDeleting={deletingId === card.item.id}
                              onDelete={() => {
                                setDeletingId(card.item.id);
                                deleteFile(card.item.id).finally(() =>
                                  setDeletingId(null)
                                );
                              }}
                            />
                          );
                        })}
                      </Stack>
                    )}

                    <Stack gap={3} mt="20px" w="330px">
                      <Text
                        fontSize="16px"
                        fontWeight={500}
                        color="#373f41"
                        lineHeight="22px"
                      >
                        Unggah Bukti Transfer
                      </Text>

                      {remainingFileSlots > 0 ? (
                        <DropzoneOcr
                          disabled={isSubmitting}
                          maxFiles={remainingFileSlots}
                          maxSizeMb={5}
                          onReady={handleDropzoneReady}
                          onFilesSelected={uploadFiles}
                        />
                      ) : (
                        <Flex
                          align="center"
                          bg="#F1F8FF"
                          borderRadius="8px"
                          justify="center"
                          minH="96px"
                          px={4}
                        >
                          <Text color="blue" fontSize="16px" fontWeight={600}>
                            Maksimal 5 file telah terupload
                          </Text>
                        </Flex>
                      )}
                    </Stack>

                    <Flex justify="flex-end" mt="16px" w="330px">
                      <SharedButton
                        variant="solid"
                        h="49px"
                        minW="144px"
                        width="144px"
                        isDisabled={!canSubmit || isSubmitting}
                        isLoading={isSubmitting}
                        onClick={submitProof}
                      >
                        <span>Kirim</span>
                      </SharedButton>
                    </Flex>
                  </>
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </CardBody>
      </Card>
    </FormProvider>
  );
};
